// animdata.ts ↔ game/Animation.cpp's RENDERING arm (AnimationData::Init's
// m_aptbm build + DrawFrame + GetStripIndex over the RAW .anir file).
//
// The SIM consumes extracted strip/frame metadata (sim/data/anir.json — frame
// counts, hold times, special points: hash-relevant timing). The CLIENT reads
// the same RAW .anir bytes the C++ maps (big-endian: AnimationFileHeader
// { u32 cstpd; u32 aoffStpd[cstpd] }; StripData { char szName[26]; u8 cDelay;
// u8 bfFlags; u16 cfrmd; FrameData[] }; FrameData (139 bytes) { char szName[64];
// char szName2[64]; u16 ibm; u16 ibm2; u8 cHold; s8 xOrigin; s8 yOrigin;
// s8 xOrigin2; s8 yOrigin2; u8 bCustomData1; u8 bCustomData2 }) and resolves the
// frame bitmap NAMES into atlas TBitmaps (ibm/ibm2 index the list).

import type { TexAtlasMgr, DibBitmapLike, Rect, Side } from './texatlasmgr.ts';
import type { TBitmap } from './tbitmap.ts';
import type { PackFileReader } from '../io/packfile.ts';

const KCB_STRIPDATA_HEADER = 30; // szName[26] + cDelay + bfFlags + cfrmd
const KCB_FRAMEDATA = 139;

export interface ClientFrameData {
  ibm: number;
  ibm2: number;
  cHold: number;
  xOrigin: number;
  yOrigin: number;
  xOrigin2: number;
  yOrigin2: number;
  bCustomData1: number;
  bCustomData2: number;
  szName: string;
  szName2: string;
}

export interface ClientStripData {
  szName: string;
  cDelay: number;
  bfFlags: number;
  frames: ClientFrameData[];
}

const s8 = (v: number): number => (v << 24) >> 24;

function cstr(b: Uint8Array, off: number, max: number): string {
  let s = '';
  for (let i = 0; i < max && b[off + i] !== 0; i++) s += String.fromCharCode(b[off + i]);
  return s;
}

export class ClientAnimationData {
  strips: ClientStripData[] = [];
  private m_aptbm: (TBitmap | null)[] = [];

  // game/Animation.cpp:44-120 — parse the raw .anir + build the TBitmap list
  Init(pakr: PackFileReader, tam: TexAtlasMgr, pszAniName: string): boolean {
    const b = pakr.MapFile(pszAniName);
    if (b === null) return false;

    const cstpd = ((b[0] << 24) | (b[1] << 16) | (b[2] << 8) | b[3]) >>> 0;
    this.strips = [];
    let ctbm = 0;
    for (let i = 0; i < cstpd; i++) {
      const off =
        ((b[4 + i * 4] << 24) | (b[5 + i * 4] << 16) | (b[6 + i * 4] << 8) | b[7 + i * 4]) >>> 0;
      const szName = cstr(b, off, 26);
      const cDelay = b[off + 26];
      const bfFlags = b[off + 27];
      const cfrmd = (b[off + 28] << 8) | b[off + 29];
      const frames: ClientFrameData[] = [];
      for (let f = 0; f < cfrmd; f++) {
        const fo = off + KCB_STRIPDATA_HEADER + f * KCB_FRAMEDATA;
        const frame: ClientFrameData = {
          szName: cstr(b, fo, 64),
          szName2: cstr(b, fo + 64, 64),
          ibm: (b[fo + 128] << 8) | b[fo + 129],
          ibm2: (b[fo + 130] << 8) | b[fo + 131],
          cHold: b[fo + 132],
          xOrigin: s8(b[fo + 133]),
          yOrigin: s8(b[fo + 134]),
          xOrigin2: s8(b[fo + 135]),
          yOrigin2: s8(b[fo + 136]),
          bCustomData1: b[fo + 137],
          bCustomData2: b[fo + 138],
        };
        if (frame.ibm !== 65535) ctbm++;
        if (frame.ibm2 !== 65535) ctbm++;
        frames.push(frame);
      }
      this.strips.push({ szName, cDelay, bfFlags, frames });
    }

    // m_aptbm[ibm] = CreateTBitmap(frame name) (Animation.cpp:93-118)
    this.m_aptbm = new Array(ctbm).fill(null);
    for (const stpd of this.strips) {
      for (const f of stpd.frames) {
        if (f.ibm !== 65535 && this.m_aptbm[f.ibm] === null) {
          this.m_aptbm[f.ibm] = tam.CreateTBitmap(f.szName);
        }
        if (f.ibm2 !== 65535 && this.m_aptbm[f.ibm2] === null) {
          this.m_aptbm[f.ibm2] = tam.CreateTBitmap(f.szName2);
        }
      }
    }
    return true;
  }

  GetStripCount(): number {
    return this.strips.length;
  }
  GetFrameCount(nStrip: number): number {
    return this.strips[nStrip].frames.length;
  }
  tbm(i: number): TBitmap | null {
    return this.m_aptbm[i] ?? null;
  }

  // A pre-v5 recording's sim runs the LEGACY andy strip order (anim-registry
  // legacyAndyStripOrder) while this class always reads the mounted pack's FIXED
  // order, so at the two indices where the layouts' frame counts differ the sim's
  // frame index can exceed the drawn strip's count — clamp instead of crashing
  // (the viewer draws the last frame; sim timing is untouched).
  private frame(nStrip: number, nFrame: number): ClientFrameData {
    const frames = this.strips[nStrip].frames;
    return nFrame < frames.length ? frames[nFrame] : frames[frames.length - 1];
  }

  // game/Animation.cpp:215-226 — second bitmap UNDER the first (ibm2 drawn first)
  DrawFrame(
    nStrip: number,
    nFrame: number,
    pbm: DibBitmapLike,
    x: number,
    y: number,
    side: Side,
    prcSrc: Rect | null = null,
    opacity = 255,
  ): void {
    const pfrmd = this.frame(nStrip, nFrame);
    if (pfrmd.ibm2 !== 65535) {
      this.m_aptbm[pfrmd.ibm2]?.BltTo(
        pbm,
        x - pfrmd.xOrigin2,
        y - pfrmd.yOrigin2,
        side,
        prcSrc,
        opacity,
      );
    }
    if (pfrmd.ibm !== 65535) {
      this.m_aptbm[pfrmd.ibm]?.BltTo(
        pbm,
        x - pfrmd.xOrigin,
        y - pfrmd.yOrigin,
        side,
        prcSrc,
        opacity,
      );
    }
  }

  // game/Animation.cpp:132-140
  GetFrameOrigin(nStrip: number, nFrame: number): { x: number; y: number } {
    const pfrmd = this.frame(nStrip, nFrame);
    return { x: pfrmd.xOrigin, y: pfrmd.yOrigin };
  }

  // game/Animation.cpp:174-213 — the frame's pixel bbox (union of both layers)
  GetBounds(
    nStrip: number,
    nFrame: number,
  ): { left: number; top: number; right: number; bottom: number } {
    const pfrmd = this.frame(nStrip, nFrame);
    const prc = { left: 0, top: 0, right: 0, bottom: 0 };
    if (pfrmd.ibm !== 65535) {
      const siz = this.m_aptbm[pfrmd.ibm]?.GetSize() ?? { cx: 0, cy: 0 };
      prc.left = -pfrmd.xOrigin;
      prc.right = prc.left + siz.cx;
      prc.top = -pfrmd.yOrigin;
      prc.bottom = prc.top + siz.cy;
    }
    if (pfrmd.ibm2 !== 65535) {
      const siz = this.m_aptbm[pfrmd.ibm2]?.GetSize() ?? { cx: 0, cy: 0 };
      const xL = -pfrmd.xOrigin2;
      if (prc.left > xL) prc.left = xL;
      const yT = -pfrmd.yOrigin2;
      if (prc.top > yT) prc.top = yT;
      if (prc.right < xL + siz.cx) prc.right = xL + siz.cx;
      if (prc.bottom < yT + siz.cy) prc.bottom = yT + siz.cy;
    }
    return prc;
  }

  // game/Animation.cpp:228-241
  GetStripIndex(pszStripName: string): number {
    for (let i = 0; i < this.strips.length; i++) {
      if (this.strips[i].szName.toLowerCase() === pszStripName.toLowerCase()) return i;
    }
    return -1;
  }
}
